/**
 * DetailViewManager — handles expand/collapse of detail rows in an Argus Table.
 *
 * When a row is expanded, a detail `<tr>` is inserted directly below the parent
 * row containing a single `<td>` that spans all columns. The cell content is
 * produced by the `detailFormatter` function. The `detailFilter` function
 * determines which rows are eligible for expansion.
 */
export class DetailViewManager {
  /**
   * @param {Object} options
   * @param {Function} options.detailFilter - (row) => boolean — returns true if the row is expandable
   * @param {Function} options.detailFormatter - (row) => string — returns HTML content for the detail area
   */
  constructor({ detailFilter, detailFormatter } = {}) {
    this._detailFilter = typeof detailFilter === 'function' ? detailFilter : () => false
    this._detailFormatter = typeof detailFormatter === 'function' ? detailFormatter : () => ''
    /** @type {Map<number, HTMLTableRowElement>} Maps row index → inserted detail <tr> */
    this._expanded = new Map()
  }

  /**
   * Returns true if the given row is expandable according to the detailFilter.
   *
   * @param {Object} row
   * @returns {boolean}
   */
  isExpandable(row) {
    return !!this._detailFilter(row)
  }

  /**
   * Toggle the detail row for the given index. If the row is currently collapsed,
   * a detail `<tr>` is inserted below `parentTr`. If already expanded, the detail
   * row is removed.
   *
   * @param {number} rowIndex
   * @param {Object} row - The row data object
   * @param {HTMLTableRowElement} parentTr - The parent `<tr>` element in the DOM
   */
  toggleRow(rowIndex, row, parentTr) {
    if (this._expanded.has(rowIndex)) {
      // Collapse — remove the detail row
      const detailTr = this._expanded.get(rowIndex)
      if (detailTr.parentNode) {
        detailTr.parentNode.removeChild(detailTr)
      }
      this._expanded.delete(rowIndex)
      return
    }

    // Expand — insert a detail row below the parent
    const detailTr = document.createElement('tr')
    detailTr.className = 'argus-detail-row'

    const td = document.createElement('td')
    // Span all columns — count cells in the parent row
    const colSpan = parentTr.cells ? parentTr.cells.length : 1
    td.setAttribute('colspan', String(colSpan))
    td.innerHTML = this._detailFormatter(row)

    detailTr.appendChild(td)

    // Insert immediately after the parent row
    if (parentTr.nextSibling) {
      parentTr.parentNode.insertBefore(detailTr, parentTr.nextSibling)
    } else {
      parentTr.parentNode.appendChild(detailTr)
    }

    this._expanded.set(rowIndex, detailTr)
  }

  /**
   * Collapse all currently expanded detail rows and clear internal state.
   */
  collapseAll() {
    this._expanded.forEach((detailTr) => {
      if (detailTr.parentNode) {
        detailTr.parentNode.removeChild(detailTr)
      }
    })
    this._expanded.clear()
  }

  /**
   * Return an array of row indices that are currently expanded.
   *
   * @returns {Array<number>}
   */
  getExpandedIndices() {
    return Array.from(this._expanded.keys())
  }
}
